import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icons from '../Icons';
import { mapChildrenTypes, getDisplayName } from './cardlayout-utils';
import { CardLayoutToolbar, CardLayoutHeader, CardLayoutTabs, CardLayoutContent } from './CardLayoutComponents';
import * as S from './CardLayoutStyled';

const TOP_THRESHOLD = 8; // within this many px of the top, always show the header
const COLLAPSE_AFTER = 64; // only start collapsing once scrolled past this depth
const DIRECTION_DELTA = 6; // px of movement required to count as an intentional scroll

export const CardLayout = ({ children, className }) => {
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const lastScrollTop = useRef(0);
  const lastClientHeight = useRef(0);
  const lastScrollHeight = useRef(0);
  const isTicking = useRef(false);
  const childMap = mapChildrenTypes(children);

  const toolbar = childMap[getDisplayName(CardLayoutToolbar)];
  const header = childMap[getDisplayName(CardLayoutHeader)];
  const tabs = childMap[getDisplayName(CardLayoutTabs)];
  const content = childMap[getDisplayName(CardLayoutContent)];

  const collapsible = header?.props?.collapsible ?? true;
  const collapseOnScroll = header?.props?.collapseOnScroll ?? false;
  const headerHeight = header?.props?.height ?? 'auto';

  // Collapses the header when the user scrolls down and shows it again when they scroll up.
  const handleScroll = useCallback(e => {
    // Run at most once per frame: ignore extra scroll events until the queued frame finishes.
    if (isTicking.current) return;
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    isTicking.current = true;
    requestAnimationFrame(() => {
      // How far and in which direction the user scrolled since the last handled event.
      const delta = scrollTop - lastScrollTop.current;
      // Layout changes move scrollTop on their own: the header expanding/collapsing resizes this
      // region (clientHeight), and content growing/shrinking re-clamps the scroll offset
      // (scrollHeight). Neither is a user scroll, so don't let them toggle the header.
      const resized = clientHeight !== lastClientHeight.current || scrollHeight !== lastScrollHeight.current;

      // Remember this event's values as the baseline for the next one.
      lastScrollTop.current = scrollTop;
      lastClientHeight.current = clientHeight;
      lastScrollHeight.current = scrollHeight;
      isTicking.current = false;

      if (resized) return; // height-change echo, not a user scroll — leave the header as-is
      if (scrollTop <= TOP_THRESHOLD) {
        setIsHeaderExpanded(true); // at the top → always show the header
      } else if (delta > DIRECTION_DELTA && scrollTop > COLLAPSE_AFTER) {
        setIsHeaderExpanded(false); // scrolled down far enough → hide the header
      } else if (delta < -DIRECTION_DELTA) {
        setIsHeaderExpanded(true); // scrolled up → show the header
      }
    });
  }, []);

  return (
    <S.Container className={className}>
      {toolbar}
      <S.HeaderSection>
        <S.CollapsibleHeaderWrapper
          initial={false}
          animate={{ height: isHeaderExpanded ? headerHeight : 0, opacity: isHeaderExpanded ? 1 : 0 }}>
          {header}
        </S.CollapsibleHeaderWrapper>
        {collapsible && (
          <S.CollapseToggle
            $expanded={isHeaderExpanded}
            data-testid="collapse-header-toggle"
            onClick={() => setIsHeaderExpanded(prev => !prev)}>
            <Icons.Collapse className="collapse-icon" />
          </S.CollapseToggle>
        )}
      </S.HeaderSection>
      <S.TabsSection>{tabs}</S.TabsSection>
      {collapseOnScroll ? <S.ContentSection onScroll={handleScroll}>{content}</S.ContentSection> : content}
    </S.Container>
  );
};

CardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  gap: PropTypes.string
};
